import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '@/lib/settings-categories';
import { ChevronDown, ChevronRight, AlertCircle, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';

interface SettingsReaderProps {
  categorized: Record<string, Record<string, unknown>> | null;
  loading: boolean;
  error: string | null;
  readAt: string | null;
  onRefresh: () => void;
}

export function SettingsReader({ categorized, loading, error, readAt, onRefresh }: SettingsReaderProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['battery', 'grid']));

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  if (loading) {
    return <SettingsReaderSkeleton />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-wt-red-primary/20 bg-wt-red-primary/5 p-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-wt-red-primary flex-shrink-0" />
          <p className="text-sm text-wt-red-primary">{error}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          className="mt-2 border-wt-border-default text-wt-text-secondary"
        >
          <RefreshCw className="h-3 w-3 mr-1.5" />
          Retry
        </Button>
      </div>
    );
  }

  if (!categorized) {
    return (
      <div className="rounded-lg border border-wt-border-subtle bg-wt-bg-secondary p-6 text-center">
        <p className="text-sm text-wt-text-muted">
          No settings loaded. Click "Read Settings" to fetch current inverter configuration.
        </p>
      </div>
    );
  }

  const sortedCategories = CATEGORY_ORDER.filter(
    c => categorized[c] && Object.keys(categorized[c]).length > 0
  );

  return (
    <div className="space-y-1">
      {readAt && (
        <p className="text-xs text-wt-text-disabled mb-2">
          Last read: {new Date(readAt).toLocaleString()}
        </p>
      )}
      {sortedCategories.map(cat => {
        const entries = Object.entries(categorized[cat]);
        const expanded = expandedCategories.has(cat);
        return (
          <div key={cat} className="rounded-md border border-wt-border-subtle bg-wt-bg-secondary">
            <button
              onClick={() => toggleCategory(cat)}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-wt-bg-tertiary/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                {expanded
                  ? <ChevronDown className="h-3.5 w-3.5 text-wt-text-muted" />
                  : <ChevronRight className="h-3.5 w-3.5 text-wt-text-muted" />
                }
                <span className="text-sm font-medium text-wt-text-primary">
                  {CATEGORY_LABELS[cat] ?? cat}
                </span>
              </div>
              <Badge variant="outline" className="text-[10px] border-wt-border-subtle text-wt-text-disabled">
                {entries.length}
              </Badge>
            </button>
            {expanded && (
              <div className="border-t border-wt-border-subtle divide-y divide-wt-border-subtle">
                {entries.map(([key, value]) => (
                  <div key={key} className="px-3 py-1.5 flex items-center justify-between gap-4">
                    <span className="text-xs text-wt-text-secondary truncate">{key}</span>
                    <span className="text-xs font-mono text-wt-text-primary flex-shrink-0">
                      {value === null || value === undefined ? '—' : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SettingsReaderSkeleton() {
  return (
    <div className="space-y-1">
      {[1, 2, 3].map(i => (
        <div key={i} className="rounded-md border border-wt-border-subtle bg-wt-bg-secondary p-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-3.5 w-3.5 rounded" />
            <Skeleton className="h-4 w-24 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
